import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Extracts a user-friendly error message from Supabase edge function errors.
 * Edge functions return error details in the response body, but Supabase client
 * wraps non-2xx responses with a generic "Edge Function returned a non-2xx status code" message.
 * This function attempts to extract the actual error message from the response.
 */
export function getEdgeFunctionErrorMessage(error: any): string {
  // If error is already a user-friendly string, return it
  if (typeof error === 'string') {
    return error
  }

  // Try to extract error from response body
  // Supabase client may include the response in various places
  let responseBody: any = null
  
  // Try error.context.body (common in Supabase error structure)
  if (error?.context?.body) {
    try {
      responseBody = typeof error.context.body === 'string' 
        ? JSON.parse(error.context.body) 
        : error.context.body
    } catch {
      // If parsing fails, continue with other methods
    }
  }
  
  // Try error.data (alternative location)
  if (!responseBody && error?.data) {
    try {
      responseBody = typeof error.data === 'string' 
        ? JSON.parse(error.data) 
        : error.data
    } catch {
      // If parsing fails, continue with other methods
    }
  }

  // Try error.context.response (some Supabase versions)
  if (!responseBody && error?.context?.response) {
    try {
      const response = error.context.response
      if (typeof response === 'string') {
        responseBody = JSON.parse(response)
      } else if (response?.body) {
        responseBody = typeof response.body === 'string' 
          ? JSON.parse(response.body) 
          : response.body
      }
    } catch {
      // If parsing fails, continue with other methods
    }
  }

  // Check for user-friendly error fields in response body
  if (responseBody) {
    // Prefer userMessage, then details, then error field
    if (responseBody.userMessage) {
      return responseBody.userMessage
    }
    if (responseBody.details) {
      return responseBody.details
    }
    if (responseBody.error) {
      // If error field exists but is technical, try to make it more user-friendly
      const errorText = typeof responseBody.error === 'string' 
        ? responseBody.error 
        : String(responseBody.error)
      
      // Check if it's already user-friendly (doesn't contain technical terms)
      if (!errorText.includes('non-2xx') && 
          !errorText.includes('status code') && 
          !errorText.includes('Edge Function')) {
        return errorText
      }
    }
  }

  // Check error.message for user-friendly content
  const errorMessage = error?.message || String(error) || ''
  
  // If message contains the generic Supabase error, provide a better message
  if (errorMessage.includes('non-2xx') || 
      errorMessage.includes('status code') || 
      errorMessage.includes('Edge Function returned')) {
    return 'We had a problem processing your request. Please try again.'
  }

  // If message is already user-friendly, return it
  if (errorMessage && 
      !errorMessage.includes('Edge Function') && 
      !errorMessage.includes('non-2xx')) {
    return errorMessage
  }

  // Fallback to generic user-friendly message
  return 'We had a problem processing your request. Please try again.'
}
